import { useCallback, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Button from 'components/Button'
import {
  Report17CreateFormSchemas,
  Report17CSVDataSchemas,
  Report17CreateFormTypes,
  Report17CSVDataTypes,
} from 'schemas/report-17-schemas'

import { useReport17Mutation } from './use-report-17'
import { ExportToXLSX } from '../../utils-report-17-xlsx-actions'

export const CreateReport17Form = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const resultRef = useRef<HTMLDivElement | null>(null)

  const date = new Date().toISOString()
  const formDefaultValues: Report17CreateFormTypes = {
    startDate: date,
    endDate: date,
    test: 0,
    fromTraderId: null,
    reportTypeId: 1,
  }

  const form = useForm<Report17CreateFormTypes>({
    resolver: zodResolver(Report17CreateFormSchemas),
    defaultValues: formDefaultValues,
  })

  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  const {
    data: report17Data,
    mutateAsync: postReport17Mutation,
    isSuccess,
    isError,
    isLoading,
  } = useReport17Mutation()

  const onSubmit = useCallback(
    async (formData: Report17CreateFormTypes) => {
      const formattedData = {
        ...formData,
        startDate: `${formData.startDate}`,
        endDate: `${formData.endDate}`,
      }
      postReport17Mutation(formattedData)
    },
    [postReport17Mutation]
  )

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.startDate?.message}
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.endDate?.message}
            />
          )}
        />
      </div>
      <div className="flex gap-4 justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoading}
        >
          <span className="tracking-wider font-bold">
            {isLoading ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>

        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>

      {isError || (isSuccess && !report17Data?.data.length) ? (
        <div
          ref={resultRef}
          className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
        >
          <div>
            <p className="tracking-wide text-red font-light">
              {`Sorry, there are no items available.`}
            </p>
          </div>
        </div>
      ) : null}

      {isSuccess && report17Data?.data.length ? (() => {
        const csvKeys = Object.keys(Report17CSVDataSchemas.shape)
        const headerMapping: Record<string, string> = {
          salesCentre: 'Centre',
          salesCentreUser: 'User',
          ident: 'CardNumber',
          transactionDate: 'TransactionDate',
          giftCardSales: 'GiftCardSales',
          serviceFees: 'ServiceFees',
          cardCarrier: 'CardCarrierFees',
          customGiftCardFees: 'CustomGiftCardFees',
          seasonalGiftCardFees: 'SeasonalGiftCardFees',
          courier: 'Courier',
          administrationFees: 'AdministrationFees',
          busTickets: 'BusTickets',
          carparks: 'Carparks',
          kiddyCruisers: 'KiddyCruisers',
          totalSum: 'Total',
          tenderCashSum: 'Cash',
          tenderEftposSum: 'Eftpos',
          tenderVisaSum: 'Visa',
          tenderMcardSum: 'MasterCard',
          tenderAmexSum: 'Amex',
          tenderDinersSum: 'Diners',
          tenderGiftCardSum: 'GiftCards',
          tenderDirectCreditSum: 'DirectCredit',
          tenderAllSum: 'TenderTotal',
          orderColumn: 'GroupID',
        }

        const currencyKeys = new Set([
          'giftCardSalesSum',
          'serviceFees',
          'cardCarrier',
          'customGiftCardFees',
          'seasonalGiftCardFees',
          'courier',
          'administrationFees',
          'busTickets',
          'carparks',
          'kiddyCruisers',
          'totalSum',
          'tenderCashSum',
          'tenderEftposSum',
          'tenderVisaSum',
          'tenderMcardSum',
          'tenderAmexSum',
          'tenderDinersSum',
          'tenderGiftCardSum',
          'tenderDirectCreditSum',
          'tenderAllSum',
        ])

        const formatCurrency = (value: unknown): string => {
          if (value === null || value === undefined || value === '') return ''

          const num = Number(value)
          if (Number.isNaN(num)) return String(value)

          return num.toLocaleString('en-NZ', {
            style: 'currency',
            currency: 'NZD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        }

        const csvData = report17Data.data.map((row: Report17CSVDataTypes) =>
          Object.fromEntries(
            csvKeys.map((key) => {
              const mappedKey = headerMapping[key] || key
              const value = row[key as keyof Report17CSVDataTypes]
              return [
                mappedKey,
                currencyKeys.has(key)
                  ? formatCurrency(value)
                  : value,
              ]
            })
          )
        )

        return (
          <div
            ref={resultRef}
            className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
          >
            <div>
              <p className="font-bold text-lg tracking-wide text-primary">
                Results:
              </p>
              <p className="tracking-wide text-primary font-light">
                The report is ready to download.
              </p>
            </div>

            <Button
              type="button"
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={() =>
                ExportToXLSX(csvData, {
                  filename: `Report17_${form.getValues('startDate')}_${form.getValues('endDate')}.xlsx`,
                })
              }
            >
              Download Report
            </Button>
          </div>
        )
      })() : null}
    </form>
  )
}
